import type { Template } from '@/types/template';

/**
 * A row in the templates grid: the shared group-level template, optionally followed by this
 * company's override of it. Only the current company's overrides are visible, so a parent has at
 * most one child.
 */
export interface TemplateTreeRow {
  template: Template;
  isChild: boolean;
  /** True on a parent this company has customised, so the parent row can say the company differs. */
  hasChild: boolean;
}

/**
 * Groups a flat page of templates so each override follows the shared template it overrides.
 *
 * An override whose parent is not on the page - filtered out by the search, or on another page -
 * stays a top-level row rather than disappearing.
 */
export function buildTemplateTree(templates: readonly Template[]): TemplateTreeRow[] {
  const childrenByParentId = new Map<string, Template>();

  for (const template of templates) {
    if (template.sourceTemplateId && templates.some((x) => x.id === template.sourceTemplateId)) {
      childrenByParentId.set(template.sourceTemplateId, template);
    }
  }

  const placedChildIds = new Set([...childrenByParentId.values()].map((x) => x.id));

  return templates.filter((x) => !placedChildIds.has(x.id)).flatMap((x) => toRows(x, childrenByParentId.get(x.id)));
}

function toRows(template: Template, child: Template | undefined): TemplateTreeRow[] {
  const row: TemplateTreeRow = {
    template,
    isChild: Boolean(template.sourceTemplateId),
    hasChild: Boolean(child),
  };

  return child ? [row, { template: child, isChild: true, hasChild: false }] : [row];
}
